/**
 * @file 과업 요청서 API
 * 티켓(요청서) CRUD + 버전(리비전) + 피드백 + 요청 유형·양식 + 업무 메뉴얼.
 *
 * 파일이 오가는 엔드포인트는 multipart 다:
 *  - 요청 등록 / 새 버전 등록 → data(JSON Blob) + files[] 를 한 번에 보낸다.
 *    ★ data 를 `Blob([...], {type:"application/json"})` 으로 감싸는 게 핵심 —
 *      그냥 문자열로 append 하면 Content-Type 이 text/plain 이 되어
 *      서버의 @RequestPart 역직렬화가 415 로 실패한다.
 */
import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const B  = `${API_BASE}/work-requests`;
const BT = `${API_BASE}/work-request-types`;
const BM = `${API_BASE}/work-manuals`;

/** JSON 파트 + 파일들을 하나의 FormData 로 조립. */
function multipart(data, files) {
    const form = new FormData();
    if (data !== undefined && data !== null) {
        form.append("data", new Blob([JSON.stringify(data)], { type: "application/json" }));
    }
    (files || []).forEach(f => form.append("files", f));
    return form;
}

export const workRequestApi = {
    // ─── 티켓 ────────────────────────────────────────────────────
    // params: { deptId?, status?, mine?, assigned? }
    getList: (params = {}) => api.get(B, params),
    get:     (id)          => api.get(`${B}/${id}`),

    /** 요청 등록 — 양식 작성본 1개 이상 필수(서버에서도 강제). */
    create:  (data, files) => api.upload(B, multipart(data, files)),

    update:  (id, data)    => api.put(`${B}/${id}`, data),
    remove:  (id)          => api.delete(`${B}/${id}`),
    changeAssignee: (id, assigneeId) => api.put(`${B}/${id}/assignee`, { assigneeId }),
    /** status: REQUESTED|ACCEPTED|IN_PROGRESS|REVIEW_REQUEST|REVISION_REQUEST|COMPLETED */
    changeStatus:   (id, status, reason) => api.put(`${B}/${id}/status`, { status, reason }),

    // ─── 버전(리비전) ────────────────────────────────────────────
    getRevisions: (id) => api.get(`${B}/${id}/revisions`),
    /** 담당자가 올리면 결과본(자동 '검토요청'), 요청자가 올리면 요청본. 서버가 역할로 판단한다. */
    addRevision: (id, note, files) => api.upload(`${B}/${id}/revisions`, multipart({ note }, files)),

    // ─── 첨부 ────────────────────────────────────────────────────
    /** 미리보기 (blob). PDF·이미지·엑셀·텍스트는 원본, 오피스는 서버 PDF 변환. */
    previewAttachment: (attachmentId) => api.download(`${B}/attachments/${attachmentId}/preview`),
    removeAttachment:  (attachmentId) => api.delete(`${B}/attachments/${attachmentId}`),

    // ─── 피드백 ──────────────────────────────────────────────────
    /** version = 어느 왕복 카드에서 쓴 댓글인지. 생략하면 서버가 현재 최신 버전으로 본다. */
    addComment:    (id, content, version) => api.post(`${B}/${id}/comments`, { content, version }),
    removeComment: (commentId)     => api.delete(`${B}/comments/${commentId}`),

    // ─── 요청 유형·양식 ──────────────────────────────────────────
    // deptId 를 주면 그 팀의 활성 유형만(요청 작성용), 없으면 전체(관리용). 양식 파일이 함께 온다.
    getTypes:    (deptId) => api.get(BT, deptId != null ? { deptId } : {}),
    createType:  (data)       => api.post(BT, data),
    updateType:  (id, data)   => api.put(`${BT}/${id}`, data),
    removeType:  (id)         => api.delete(`${BT}/${id}`),
    uploadFormat: (typeId, file) => {
        const form = new FormData();
        form.append("file", file);
        return api.upload(`${BT}/${typeId}/formats`, form);
    },
    removeFormat:  (formatId) => api.delete(`${BT}/formats/${formatId}`),
    previewFormat: (formatId) => api.download(`${BT}/formats/${formatId}/preview`),

    // ─── 업무 메뉴얼 ─────────────────────────────────────────────
    getManuals:   (deptId) => api.get(BM, deptId != null ? { deptId } : {}),
    createManual: (data)     => api.post(BM, data),
    updateManual: (id, data) => api.put(`${BM}/${id}`, data),
    removeManual: (id)       => api.delete(`${BM}/${id}`),
    uploadManualFile: (manualId, file) => {
        const form = new FormData();
        form.append("file", file);
        return api.upload(`${BM}/${manualId}/attachments`, form);
    },
    removeManualFile:  (attachmentId) => api.delete(`${BM}/attachments/${attachmentId}`),
    previewManualFile: (attachmentId) => api.download(`${BM}/attachments/${attachmentId}/preview`),

    // ─── 참조 데이터 ─────────────────────────────────────────────
    getDepartments: () => api.get(`${API_BASE}/employees/departments`),
    getMe:          () => api.get(`${API_BASE}/employees/me`),
    /** 전 직원 간단 목록 — 담당자를 '선택한 팀 소속' 으로 좁히는 데 쓴다.
     *  EmployeeSimpleDto: { id, name, employeeCode, deptId, deptName, positionName } */
    getEmployees:   () => api.get(`${API_BASE}/employees`),
};
